import React from "react";
import { fetchPageableProductsByIds } from "../../hooks/usePageableProducts";
import "../business/Business.css";
import "./CreateSoldOrderPage.css";
import "./CreateSoldOrderPromotionsSection.css";
import {
  normalizePromotionRewards,
  countPositiveQtyLines,
} from "./soldOrderPromotionUtils";

const Spinner = () => {
  return (
    <div style={{
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      height: "100%",
      width: "100%",
      minHeight: 40,
      minWidth: 40
    }}>
      <svg width="32" height="32" viewBox="0 0 32 32" aria-label="Loading" role="status">
        <circle
          cx="16"
          cy="16"
          r="12"
          fill="none"
          stroke="currentColor"
          strokeWidth="4"
          strokeLinecap="round"
          strokeDasharray="18.85, 18.85"
        >
          <animateTransform
            attributeName="transform"
            type="rotate"
            from="0 16 16"
            to="360 16 16"
            dur="1s"
            repeatCount="indefinite"
          />
        </circle>
      </svg>
    </div>
  );
};

function CreateSoldOrderPromotionsSection({
  branchId,
  requestWithMeta,
  productById,
  promotionQtyByPromotionId,
  onCommitPromotionQuantities,
  onPromotionDiscountsLoaded,
  onProductsLoaded,
  isRental = false,
  onPickRental,
}) {
  const discountsLoadedRef = React.useRef(onPromotionDiscountsLoaded);
  discountsLoadedRef.current = onPromotionDiscountsLoaded;
  const productsLoadedRef = React.useRef(onProductsLoaded);
  productsLoadedRef.current = onProductsLoaded;
  const [promotions, setPromotions] = React.useState([]);
  const [promotionsLoading, setPromotionsLoading] = React.useState(false);

  const [promotionModalId, setPromotionModalId] = React.useState(null);
  const [promotionModalDetail, setPromotionModalDetail] = React.useState(null);
  const [promotionModalLoading, setPromotionModalLoading] = React.useState(false);
  const [promotionModalError, setPromotionModalError] = React.useState(null);
  const [rewardProductsById, setRewardProductsById] = React.useState({});

  const [modalDraftQtyByProductId, setModalDraftQtyByProductId] = React.useState({});
  const draftSyncedForModalIdRef = React.useRef(null);

  const skeletonItems = React.useMemo(
    () => Array.from({ length: 4 }, (_, i) => i),
    []
  );

  React.useEffect(() => {
    if (!branchId) return;
    let cancelled = false;
    setPromotionsLoading(true);
    (async () => {
      try {
        const res = await requestWithMeta(`/operator/promotions`, "GET");
        if (cancelled) return;
        if (!res?.ok) {
          setPromotions([]);
          return;
        }
        const list = Array.isArray(res?.data) ? res.data : [];
        setPromotions(list.filter((p) => p?.active === true));
      } catch (_) {
        if (!cancelled) setPromotions([]);
      } finally {
        if (!cancelled) setPromotionsLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [branchId, requestWithMeta]);

  React.useEffect(() => {
    if (!promotionModalId) {
      setPromotionModalDetail(null);
      setPromotionModalError(null);
      setPromotionModalLoading(false);
      setModalDraftQtyByProductId({});
      setRewardProductsById({});
      draftSyncedForModalIdRef.current = null;
      return;
    }

    let cancelled = false;
    setPromotionModalLoading(true);
    setPromotionModalError(null);
    setPromotionModalDetail(null);
    setRewardProductsById({});
    draftSyncedForModalIdRef.current = null;

    (async () => {
      try {
        const res = await requestWithMeta(
          `/operator/promotions/${encodeURIComponent(promotionModalId)}`,
          "GET"
        );
        if (cancelled) return;
        if (!res?.ok) {
          setPromotionModalError(
            res?.data?.error || res?.data?.message || "Не удалось загрузить акцию"
          );
          setPromotionModalDetail(null);
          return;
        }
        const detail = res?.data ?? {};
        const { rows, discountByProductId } = normalizePromotionRewards(detail);
        const productIds = rows.map((row) => row.productId).filter((id) => id != null);

        if (productIds.length > 0) {
          const fetched = await fetchPageableProductsByIds({
            requestWithMeta,
            productIds,
            branchId,
            flatten: true,
          });
          if (cancelled) return;
          if (!fetched.ok) {
            setPromotionModalError(fetched.error || "Не удалось загрузить товары акции");
            setPromotionModalDetail(null);
            return;
          }
          const nextById = {};
          (fetched.list || []).forEach((p) => {
            const id = p?._id ?? p?.id;
            if (id == null) return;
            nextById[String(id)] = p;
          });
          setRewardProductsById(nextById);
          productsLoadedRef.current?.(fetched.list);
        }

        if (cancelled) return;
        setPromotionModalDetail(detail);
        discountsLoadedRef.current?.(String(promotionModalId), discountByProductId);
      } catch (_) {
        if (!cancelled) {
          setPromotionModalError("Ошибка загрузки акции");
          setPromotionModalDetail(null);
        }
      } finally {
        if (!cancelled) setPromotionModalLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [promotionModalId, requestWithMeta, branchId]);

  React.useEffect(() => {
    if (!promotionModalId || promotionModalLoading || !promotionModalDetail) return;
    if (draftSyncedForModalIdRef.current === promotionModalId) return;
    draftSyncedForModalIdRef.current = promotionModalId;
    const committed = promotionQtyByPromotionId[String(promotionModalId)] || {};
    setModalDraftQtyByProductId({ ...committed });
  }, [
    promotionModalId,
    promotionModalLoading,
    promotionModalDetail,
    promotionQtyByPromotionId,
  ]);

  const promotionModalRewardRows = React.useMemo(() => {
    if (!promotionModalDetail) return [];
    const { rows } = normalizePromotionRewards(promotionModalDetail);
    return rows
      .map(({ productId, discount }) => {
        const p = rewardProductsById[String(productId)] || productById[String(productId)];
        return {
          productId,
          discount,
          product: p,
          name: p?.name ?? `Товар ${productId}`,
          imageUrl: p?.imageUrl ?? "",
          stockQty: p?.quantity ?? 0,
        };
      })
      .filter((r) => r.productId != null);
  }, [promotionModalDetail, productById, rewardProductsById]);

  function openPromotionModal(promotionId) {
    draftSyncedForModalIdRef.current = null;
    setPromotionModalId(String(promotionId));
  }

  function closePromotionModal() {
    setPromotionModalId(null);
  }

  function setDraftQty(productId, value) {
    const key = String(productId);
    setModalDraftQtyByProductId((prev) => ({
      ...prev,
      [key]: value,
    }));
  }

  function handleApprove() {
    if (!promotionModalId) return;
    onCommitPromotionQuantities?.(promotionModalId, modalDraftQtyByProductId);
    closePromotionModal();
  }

  const modalDraftSelectedCount = countPositiveQtyLines(modalDraftQtyByProductId);

  const modalTitle =
    promotionModalDetail?.name ??
    promotions.find((x) => String(x?.id ?? x?._id) === String(promotionModalId))?.name ??
    "Акция";

  const approveDisabled =
    promotionModalLoading ||
    !!promotionModalError ||
    promotionModalRewardRows.length === 0;

  if (promotionsLoading) {
    return (
      <section className="create-sold-order-promotionsSection">
        <div className="shop-create-label" style={{ marginBottom: 5, marginTop: 8 }}>
          Активные акции
        </div>
        <Spinner />
      </section>
    );
  }

  if (promotions.length === 0) {
    return null;
  }

  return (
    <>
      <section className="create-sold-order-promotionsSection">
        <div className="shop-create-label" style={{ marginBottom: 5, marginTop: 8 }}>
          Активные акции
        </div>
        <div className="create-sold-order-promotions">
          <div className="create-sold-order-promotionsHint">
            Нажмите акцию, чтобы выбрать товары со скидкой.
          </div>
          {promotions.map((promo) => {
            const id = promo?.id ?? promo?._id;
            if (id == null) return null;
            const name = promo?.name ?? `Акция #${id}`;
            const committedInner = promotionQtyByPromotionId[String(id)] || {};
            const committedCount = countPositiveQtyLines(committedInner);
            return (
              <button
                key={String(id)}
                type="button"
                className="create-sold-order-promotionRow"
                onClick={() => openPromotionModal(id)}
              >
                <div className="create-sold-order-promotionRowText">
                  <span className="create-sold-order-promotionName">{name}</span>
                  {committedCount > 0 ? (
                    <span className="create-sold-order-promotionCountBadge">
                      Выбрано: {committedCount}
                    </span>
                  ) : null}
                </div>
                <span className="create-sold-order-promotionChevron" aria-hidden>
                  ›
                </span>
              </button>
            );
          })}
        </div>
      </section>

      {promotionModalId ? (
        <div
          className="create-sold-order-promoModal-overlay"
          role="dialog"
          aria-modal="true"
          aria-labelledby="create-sold-order-promo-modal-title"
          onClick={closePromotionModal}
        >
          <div
            className="create-sold-order-promoModal-sheet"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="create-sold-order-promoModal-header">
              <div>
                <h2
                  id="create-sold-order-promo-modal-title"
                  className="create-sold-order-promoModal-title"
                >
                  {modalTitle}
                </h2>
                <p className="create-sold-order-promoModal-sub">
                  Товары по акции
                </p>
              </div>
              <button
                type="button"
                className="create-sold-order-promoModal-close"
                onClick={closePromotionModal}
                aria-label="Закрыть без сохранения"
              >
                ×
              </button>
            </div>

            <div className="create-sold-order-promoModal-body">
              {promotionModalLoading ? (
                skeletonItems.map((item) => (
                  <div key={item} className="create-sold-order-item create-sold-order-item--skeleton">
                    <div className="shop-create-skeleton create-sold-order-skeletonImage" />
                    <div className="shop-create-skeleton shop-create-skeleton--text create-sold-order-skeletonName" />
                    <div className="shop-create-skeleton create-sold-order-skeletonInput" />
                  </div>
                ))
              ) : promotionModalError ? (
                <div className="shop-create-help">{promotionModalError}</div>
              ) : promotionModalRewardRows.length === 0 ? (
                <div className="shop-create-help">В этой акции нет товаров со скидкой.</div>
              ) : (
                <div className="create-sold-order-promoModal-list">
                  {promotionModalRewardRows.map((row) => {
                    const inputQty = modalDraftQtyByProductId[String(row.productId)] ?? "";
                    return (
                      <label key={String(row.productId)} className="create-sold-order-item">
                        <img
                          className="create-sold-order-image"
                          src={row.imageUrl || ""}
                          alt={row.name}
                          loading="lazy"
                        />
                        <div className="create-sold-order-info">
                          <div className="create-sold-order-name">{row.name}</div>
                          <div className="create-sold-order-stock">
                            В наличии: {row.stockQty}
                          </div>
                          <div className="create-sold-order-discountBadge">
                            Скидка {row.discount}%
                          </div>
                        </div>
                        <input
                          type="number"
                          min="0"
                          max={isRental ? undefined : row.stockQty}
                          step="1"
                          inputMode="numeric"
                          className="create-sold-order-qtyInput"
                          value={inputQty}
                          onChange={(e) => setDraftQty(row.productId, e.target.value)}
                          placeholder="0"
                          aria-label={`Количество для ${row.name}`}
                          hidden={isRental}
                        />
                        {isRental ? (
                          <button
                            type="button"
                            className="create-sold-order-qtyInput"
                            onClick={() => {
                              if (onPickRental?.(row, promotionModalId) === false) return;
                              setPromotionModalId(null);
                            }}
                          >
                            Период
                          </button>
                        ) : null}
                      </label>
                    );
                  })}
                </div>
              )}
            </div>

            <div className="create-sold-order-promoModal-footer">
              <div className="create-sold-order-promoModal-footerSummary">
                Выбрано: {modalDraftSelectedCount}
              </div>
              <button
                type="button"
                className="create-sold-order-promoModal-approve"
                onClick={handleApprove}
                disabled={approveDisabled}
              >
                Подтвердить
              </button>
            </div>
          </div>
        </div>
      ) : null}
    </>
  );
}

export default CreateSoldOrderPromotionsSection;
